import { CheckIcon } from "@heroicons/react/24/outline";
import { classNames } from "@via/utils/helpers.tsx";
import { TamarindToolSettings } from "common";
import { type FC, useState, useEffect } from "react";

interface TamarindInputSelectorProps {
  modelName: string;
  modelInputs: TamarindToolSettings[];
  onSelectionChange?: (selectedInputs: string[]) => void;
  initialSelectedInputs?: string[];
}

const TamarindInputSelector: FC<TamarindInputSelectorProps> = ({
  modelInputs,
  onSelectionChange,
  initialSelectedInputs = [],
}) => {
  const requiredInputs = modelInputs.filter(input => input.required);
  const optionalInputs = modelInputs.filter(input => !input.required);

  // Initialize selected inputs: required inputs are always selected
  const requiredInputNames = requiredInputs.map(input => input.name);
  const [selectedInputs, setSelectedInputs] = useState<string[]>(() => {
    const initial = new Set(requiredInputNames);
    initialSelectedInputs.forEach(name => initial.add(name));
    return Array.from(initial);
  });

  // Update when initialSelectedInputs changes
  useEffect(() => {
    const newSelected = new Set(requiredInputNames);
    initialSelectedInputs.forEach(name => newSelected.add(name));
    setSelectedInputs(Array.from(newSelected));
  }, [initialSelectedInputs.join(",")]);

  const isSelected = (name: string) => selectedInputs.includes(name);

  const handleToggleInput = (inputName: string, isRequired: boolean) => {
    if (isRequired) {
      // Required inputs cannot be deselected
      return;
    }

    setSelectedInputs(prev => {
      const newSelected = prev.includes(inputName)
        ? prev.filter(name => name !== inputName)
        : [...prev, inputName];

      // Notify parent of selection change
      if (onSelectionChange) {
        onSelectionChange(newSelected);
      }

      return newSelected;
    });
  };

  const renderInputCard = (
    input: TamarindToolSettings,
    isRequired: boolean
  ) => {
    const selected = isSelected(input.name);

    return (
      <div
        key={input.name}
        className={classNames(
          "flex items-start gap-3 rounded-md p-3 border transition-colors",
          isRequired
            ? "bg-gray-50 border-gray-200"
            : selected
              ? "bg-indigo-50 border-indigo-200"
              : "bg-white border-gray-200 hover:bg-gray-50"
        )}>
        {/* Checkbox */}
        <div className="flex items-center pt-0.5">
          <input
            type="checkbox"
            checked={selected}
            disabled={isRequired}
            onChange={() => handleToggleInput(input.name, isRequired)}
            className={classNames(
              "h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600",
              isRequired ? "cursor-not-allowed opacity-50" : ""
            )}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-medium text-gray-900">
              {input.name}
            </span>
            {input.type && (
              <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {input.type}
              </span>
            )}
            {isRequired && (
              <span className="inline-flex items-center rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                Required
              </span>
            )}
            {selected && !isRequired && (
              <span className="inline-flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                <CheckIcon className="h-3 w-3" />
                Selected
              </span>
            )}
            {input.default !== undefined && (
              <span className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                default: {String(input.default)}
              </span>
            )}
          </div>
          {input.description && (
            <p className="text-xs text-gray-600 mb-1">{input.description}</p>
          )}
          {input.options && input.options.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-500 mb-1">
                Available options:
              </p>
              <div className="flex flex-wrap gap-1">
                {input.options.map((option, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center rounded bg-purple-50 px-2 py-0.5 text-xs text-purple-700 border border-purple-200">
                    {String(option)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (modelInputs.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-4">
        No inputs available for this model
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Required Inputs */}
      {requiredInputs.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            Required Inputs
          </h4>
          <div className="space-y-2">
            {requiredInputs.map(input => renderInputCard(input, true))}
          </div>
        </div>
      )}

      {/* Optional Inputs */}
      {optionalInputs.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            Optional Inputs
            <span className="ml-2 text-xs font-normal text-gray-500">
              (Select to add to process)
            </span>
          </h4>
          <div className="space-y-2">
            {optionalInputs.map(input => renderInputCard(input, false))}
          </div>
        </div>
      )}

      {/* Summary */}
      {optionalInputs.length > 0 && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            <span className="font-medium">
              {
                selectedInputs.filter(
                  name => !requiredInputNames.includes(name)
                ).length
              }
            </span>{" "}
            optional input(s) selected •{" "}
            <span className="font-medium">{requiredInputs.length}</span>{" "}
            required input(s) always included
          </p>
        </div>
      )}
    </div>
  );
};

export default TamarindInputSelector;
